<template>
  <!-- Static: in-DOM structured layout with icon + title + description -->
  <div
    v-if="resolvedVariant === 'static' && modelValue"
    :data-testid="sectionTag"
    class="wasabi-toast-static"
    role="status"
    aria-live="polite"
    aria-atomic="true"
    :style="staticStyles"
  >
    <v-icon
      :data-testid="sectionTag ? `${sectionTag}-icon` : undefined"
      :icon="currentConfig.icon"
      size="18"
      :color="currentConfig.iconColor"
    />
    <div class="wasabi-toast-static__content">
      <span v-if="title" :data-testid="sectionTag ? `${sectionTag}-title` : undefined" class="label-semibold-medium">
        {{ title }}
      </span>
      <span
        v-if="description"
        v-dompurify-html="description"
        :data-testid="sectionTag ? `${sectionTag}-description` : undefined"
        class="body-regular-default"
      />
    </div>
    <WButton
      v-if="closable"
      :section-tag="sectionTag ? `${sectionTag}-close` : undefined"
      variant="icon"
      size="small"
      :icon="mdiClose"
      :label="$t('app.common.buttons.close')"
      @click="value = false"
    />
  </div>

  <!-- Inline: renders directly in DOM flow -->
  <div
    v-else-if="resolvedVariant === 'inline' && modelValue"
    :data-testid="sectionTag"
    class="wasabi-toast-inline"
    role="status"
    aria-live="polite"
    aria-atomic="true"
    :style="inlineStyles"
  >
    <span
      v-if="title"
      :data-testid="sectionTag ? `${sectionTag}-title` : undefined"
      class="label-semibold-medium"
      :style="{ color: currentConfig.iconColor }"
    >
      {{ `${title}: ` }}
    </span>
    <span
      v-dompurify-html="description"
      :data-testid="sectionTag ? `${sectionTag}-description` : undefined"
      class="body-regular-default"
    >
    </span>
  </div>

  <!-- Dismissible: teleported overlay via v-snackbar -->
  <v-snackbar
    v-else
    v-model="value"
    :data-testid="sectionTag"
    :location="getLocation"
    variant="text"
    timeout="3000"
    max-width="100vw"
    :width="getWrapperWidth"
    class="wasabi_toast"
    aria-live="polite"
    aria-atomic="true"
    :style="snackbarStyles"
  >
    <template #default>
      <v-alert
        :data-testid="sectionTag ? `${sectionTag}-alert` : undefined"
        :type="type as keyof typeof typeConfig"
        variant="flat"
        min-height="48"
        :width="getWidth"
        density="compact"
        role="status"
        class="wasabi-toast-alert"
      >
        <template #prepend>
          <v-icon
            :data-testid="sectionTag ? `${sectionTag}-icon` : undefined"
            :color="currentConfig.iconColor"
            :icon="currentConfig.icon"
            size="22"
          ></v-icon>
        </template>
        <template #title>
          <span :data-testid="sectionTag ? `${sectionTag}-title` : undefined" class="label-semibold-medium">
            {{ title }}
          </span>
        </template>
        <template #text>
          <span
            v-dompurify-html="description"
            :data-testid="sectionTag ? `${sectionTag}-description` : undefined"
            class="body-regular-default"
          >
          </span>
        </template>
        <template #close>
          <WButton
            v-if="closable"
            :section-tag="sectionTag ? `${sectionTag}-close` : undefined"
            variant="icon"
            size="small"
            :icon="mdiClose"
            :label="$t('app.common.buttons.close')"
            @click="value = false"
          />
        </template>
      </v-alert>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { mdiAlert, mdiClose, mdiFlag, mdiCheckCircle, mdiInformation } from '@mdi/js'
import { computed } from 'vue'
import { $t } from '@/plugins/i18n'
import { WButton } from '@wasabi/actions'
import { useDisplay } from 'vuetify'
import { tokens } from '@/plugins/vuetify'

export type ToastVariant = 'dismissible' | 'static' | 'inline'
export type ToastLocation = 'top' | 'top right' | 'bottom'

export interface WToastNotificationProps {
  sectionTag?: string
  modelValue?: boolean
  type?: string
  title?: string
  description?: string
  closable?: boolean
  inline?: boolean
  variant?: ToastVariant
  /** Optional viewport-edge clearance. Dismissible variant only. */
  offset?: number | string
  /** Optional placement override. Keeps the responsive default when omitted. */
  location?: ToastLocation
}

const props = withDefaults(defineProps<WToastNotificationProps>(), {
  sectionTag: 'unspecified',
  modelValue: false,
  type: 'info',
  title: '',
  description: '',
  closable: true,
  inline: false,
  variant: undefined,
  offset: undefined,
  location: undefined
})

const emit = defineEmits(['update:modelValue'])

const { xs } = useDisplay()
const isMobile = computed(() => xs.value)

const resolvedVariant = computed(() => {
  if (props.variant) return props.variant
  return props.inline ? 'inline' : 'dismissible'
})

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const typeConfig = {
  success: {
    surface: tokens.color.status.success.surface,
    border: tokens.color.status.success.border,
    iconColor: tokens.color.status.success.icon,
    icon: mdiCheckCircle
  },
  warning: {
    surface: tokens.color.status.warning.surface,
    border: tokens.color.status.warning.border,
    iconColor: tokens.color.status.warning.icon,
    icon: mdiFlag
  },
  error: {
    surface: tokens.color.status.danger.surface,
    border: tokens.color.status.danger.border,
    iconColor: tokens.color.status.danger.icon,
    icon: mdiAlert
  },
  info: {
    surface: tokens.color.status.info.surface,
    border: tokens.color.status.info.border,
    iconColor: tokens.color.status.info.icon,
    icon: mdiInformation
  }
}

const currentConfig = computed(() => {
  return typeConfig[props.type as keyof typeof typeConfig] || typeConfig.info
})

const getLocation = computed(() => {
  return props.location ?? (isMobile.value ? 'bottom' : 'top right')
})

const getWidth = computed(() => {
  return isMobile.value ? undefined : '478'
})

const getWrapperWidth = 'auto'

const normalizeToastOffset = (offset: number | string | undefined): string => {
  if (offset === undefined || offset === '') return '0px'
  if (typeof offset === 'number') return Number.isFinite(offset) ? `${offset}px` : '0px'

  const numericOffset = Number(offset)
  return Number.isFinite(numericOffset) ? `${numericOffset}px` : offset
}

// Inline variant: renders in DOM flow, styled via scoped CSS + inline token vars.
const inlineStyles = computed(() => ({
  '--w-toast-inline-surface': currentConfig.value.surface,
  '--w-toast-inline-border': currentConfig.value.border
}))

// Static variant: renders in DOM flow with icon + title/description.
const staticStyles = computed(() => ({
  '--w-toast-static-surface': currentConfig.value.surface,
  '--w-toast-static-border': currentConfig.value.border
}))

// VSnackbar merges this after Vuetify's layout styles on the teleported overlay
// root. Set the active viewport edge directly because its static location
// strategy does not consume VOverlay's offset prop.
const snackbarStyles = computed(() => {
  const clearance = normalizeToastOffset(props.offset)
  const edgeStyle = getLocation.value.startsWith('top')
    ? { paddingTop: clearance }
    : { paddingBottom: `calc(var(--v-layout-bottom, 0px) + ${clearance})` }

  return {
    '--w-toast-surface': currentConfig.value.surface,
    '--w-toast-border': currentConfig.value.border,
    ...edgeStyle
  }
})
</script>

<!-- UNSCOPED: v-snackbar teleports content outside the component tree.
     Scoped styles and v-bind() do not reach teleported content.
     Global CSS custom properties from design-tokens.scss (on :root) are used directly via var().
     Dynamic values (type-dependent surface/border and runtime clearance) are passed via :style. -->
<style lang="scss">
// The toast sits in the nav-bar band, and its overlay wrapper (z-index 10000,
// above the nav's 1000) would hit-test over the nav controls. Let clicks fall
// through the wrapper, re-enabling only the toast's own interactive hooks
// (close button, description links). Scoped to .wasabi_toast so the in-flow
// static/inline variants are untouched.
.wasabi_toast.v-overlay .v-overlay__content {
  pointer-events: none;
}

.wasabi_toast .v-alert__close,
.wasabi_toast a {
  pointer-events: auto;
}

.wasabi_toast .wasabi-toast-alert {
  background-color: var(--w-toast-surface) !important;
  border: 1px solid var(--w-toast-border) !important;
  border-radius: var(--border-radius-layer-medium) !important;
  padding: var(--spacing-inset-container-tight);
  gap: var(--spacing-gap-small);
  color: var(--color-content-text-primary) !important;
}

.wasabi-toast-static {
  display: flex;
  align-items: flex-start;
  background-color: var(--w-toast-static-surface);
  border: 1px solid var(--w-toast-static-border);
  border-radius: var(--border-radius-layer-medium);
  padding: var(--spacing-gap-medium);
  gap: var(--spacing-gap-small);
  color: var(--color-content-text-primary);
}

.wasabi-toast-static__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--spacing-gap-icon);
}

.wasabi-toast-inline {
  background-color: var(--w-toast-inline-surface);
  border: 1px solid var(--w-toast-inline-border);
  border-radius: var(--border-radius-layer-medium);
  color: var(--color-content-text-primary);
  min-height: 48px;
  padding: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.wasabi_toast .v-alert__content {
  @include tag(body-regular-default);
  color: var(--color-content-text-primary);
}

.wasabi_toast .v-alert__content .label-semibold-medium {
  @include tag(label-semibold-medium);
}

.wasabi_toast .v-alert-title {
  @include tag(label-semibold-medium);
}

.wasabi_toast .v-alert__prepend > .v-icon {
  font-size: 18px !important;
}

.wasabi_toast .v-alert__close > .v-btn {
  background-color: transparent !important;
  box-shadow: none !important;
  width: auto !important;
  min-width: auto !important;
  height: auto !important;
  padding: 0 !important;
  margin-block: 0 !important;
  border-radius: var(--border-radius-control-small) !important;
}

.wasabi_toast .v-alert__close .v-icon {
  font-size: 24px !important;
}

.wasabi_toast a {
  color: var(--color-action-primary-surface) !important;
  text-decoration: none !important;
}

.wasabi_toast a:hover {
  color: var(--color-action-primary-active) !important;
  text-decoration: none !important;
}

@media (max-width: 600px) {
  .wasabi_toast .v-snackbar__content {
    padding: 0px !important;
  }

  .wasabi_toast {
    margin: 0 auto;
  }

  .wasabi_toast .v-alert {
    width: 100vw;
    margin: auto;
    border: none !important;
  }
}
</style>
